/**
 * API service layer for asset folders, assets, and collections.
 * All calls go through the shared apiClient (auto-auth, token refresh).
 */

import { apiClient } from './api'
import type {
  FolderNode, Asset, AssetListResponse,
  Collection, CollectionListResponse, CollectionFilterConfig,
  AssetActivityResponse,
} from '../types/fileManager'

// ─── Folder API ──────────────────────────────────────────────────────────────

export const assetFolderApi = {
  /** Fetch the full folder tree for a project. */
  async getTree(projectId: string): Promise<{ folders: FolderNode[]; rootFolderId: number | null }> {
    const res = await apiClient.get('/asset-folders/tree', { params: { project_id: projectId } })
    return {
      folders: res.data.folders ?? [],
      rootFolderId: res.data.root_folder_id ?? null,
    }
  },

  /** List immediate children folders of a parent (or root). */
  async listFolders(projectId: string, parentId?: number): Promise<FolderNode[]> {
    const params: Record<string, unknown> = { project_id: projectId }
    if (parentId !== undefined) params.parent_id = parentId
    const res = await apiClient.get('/asset-folders/', { params })
    return res.data.folders ?? res.data ?? []
  },

  /** Create a new folder. */
  async createFolder(projectId: string, name: string, parentId: number, folderType: string = 'custom', createDefaultChildren: boolean = false): Promise<FolderNode> {
    const res = await apiClient.post('/asset-folders/', {
      name,
      parent_id: parentId,
      folder_type: folderType,
      create_default_children: createDefaultChildren,
    }, { params: { project_id: projectId } })
    return res.data
  },

  /** Rename / update a folder. */
  async renameFolder(folderId: number, name: string): Promise<FolderNode> {
    const res = await apiClient.patch(`/asset-folders/${folderId}`, { name })
    return res.data
  },

  /** Move a folder under a new parent. */
  async moveFolder(folderId: number, newParentId: number): Promise<FolderNode> {
    const res = await apiClient.post(`/asset-folders/${folderId}/move`, { new_parent_id: newParentId })
    return res.data
  },

  /** Delete a folder. */
  async deleteFolder(folderId: number, cascade: boolean = false): Promise<void> {
    await apiClient.delete(`/asset-folders/${folderId}`, { params: { cascade } })
  },

  /** Bootstrap date + default production folders for a project. */
  async bootstrapStructure(projectId: string, dateLabel?: string): Promise<unknown> {
    let defaultLabel = dateLabel
    if (!defaultLabel) {
      const now = new Date()
      defaultLabel = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    }
    const res = await apiClient.post('/asset-folders/bootstrap', {
      date_folder_name: defaultLabel,
    }, { params: { project_id: projectId } })
    return res.data
  },
}

// ─── Asset API ───────────────────────────────────────────────────────────────

export interface AssetListOpts {
  folderId?: number
  search?: string
  offset?: number
  limit?: number
  editorial_status?: string
  priority?: string
  color_label?: string
  tags?: string
  rating_min?: number
  asset_type?: string
}

export const assetApi = {
  /** List assets within a project, optionally filtered by folder, search, or metadata. */
  async list(
    projectId: string,
    opts: AssetListOpts = {},
  ): Promise<AssetListResponse> {
    const params: Record<string, unknown> = { project_id: projectId }
    if (opts.folderId !== undefined) params.folder_id = opts.folderId
    if (opts.search) params.search = opts.search
    if (opts.offset !== undefined) params.offset = opts.offset
    params.limit = opts.limit ?? 200
    if (opts.editorial_status) params.editorial_status = opts.editorial_status
    if (opts.priority) params.priority = opts.priority
    if (opts.color_label) params.color_label = opts.color_label
    if (opts.tags) params.tags = opts.tags
    if (opts.rating_min) params.rating_min = opts.rating_min
    if (opts.asset_type) params.asset_type = opts.asset_type
    const res = await apiClient.get('/assets/', { params })
    return res.data
  },

  /** Bulk-move assets to a target folder. */
  async bulkMove(assetIds: number[], targetFolderId: number) {
    const res = await apiClient.post('/assets/bulk-move', {
      asset_ids: assetIds,
      target_folder_id: targetFolderId,
    })
    return res.data as { moved: number; message: string }
  },

  /** Bulk-delete assets. */
  async bulkDelete(assetIds: number[]) {
    const res = await apiClient.post('/assets/bulk-delete', { asset_ids: assetIds })
    return res.data as { deleted: number; message: string }
  },

  /** Bulk-update editorial metadata on multiple assets. */
  async bulkUpdate(assetIds: number[], fields: {
    editorial_status?: string
    priority?: string
    color_label?: string
    rating?: number
    tags?: string[]
  }) {
    const res = await apiClient.post('/assets/bulk-update', {
      asset_ids: assetIds,
      ...fields,
    })
    return res.data as { updated: number; message: string }
  },

  /** Delete a single asset. */
  async delete(assetId: number): Promise<void> {
    await apiClient.delete(`/assets/${assetId}`)
  },

  /** Update asset metadata. */
  async update(assetId: number, data: Record<string, unknown>) {
    const res = await apiClient.patch(`/assets/${assetId}`, data)
    return res.data as Asset
  },

  /** Get presigned download URL. */
  async getDownloadUrl(assetId: number): Promise<{ url: string; filename: string }> {
    const res = await apiClient.get(`/assets/${assetId}/download-url`)
    return res.data
  },

  /** Get robust preview URL (may return proxy/transcoded video when needed). */
  async getPreviewUrl(assetId: number): Promise<{
    url: string
    filename: string
    content_type?: string
    source?: string
    needs_transcoding?: boolean
    transcoding_status?: string
    transcoding_reason?: string
    message?: string
  }> {
    const res = await apiClient.get(`/assets/${assetId}/preview-url`)
    return res.data
  },

  /** Get activity timeline for an asset. */
  async getActivity(assetId: number, limit: number = 50): Promise<AssetActivityResponse> {
    const res = await apiClient.get(`/assets/${assetId}/activity`, { params: { limit } })
    return res.data
  },

  /** Upload files with progress callback. Omit folderId for auto-routing by file type.
   *  Files are uploaded individually with up to 3 concurrent requests for best
   *  per-file progress tracking, independent retries, and streaming speed. */
  async upload(
    projectId: string,
    files: File[],
    opts?: {
      folderId?: number
      uploadDate?: string   // YYYY-MM-DD
      onProgress?: (pct: number) => void
      signal?: AbortSignal
    },
  ) {
    const MAX_CONCURRENT = 8

    const params = new URLSearchParams({ project_id: projectId, asset_type: 'footage' })
    if (opts?.folderId !== undefined) params.set('folder_id', String(opts.folderId))
    if (opts?.uploadDate) params.set('upload_date', opts.uploadDate)
    const url = `/assets/?${params.toString()}`

    // Track progress per file for aggregate reporting
    const fileLoaded = new Array(files.length).fill(0)
    const fileTotalBytes = files.map(f => f.size || 0)
    const grandTotal = fileTotalBytes.reduce((a, b) => a + b, 0)
    const reportProgress = () => {
      if (grandTotal > 0 && opts?.onProgress) {
        const loaded = fileLoaded.reduce((a: number, b: number) => a + b, 0)
        opts.onProgress(Math.round((loaded / grandTotal) * 100))
      }
    }

    const uploadOne = async (file: File, fileIndex: number) => {
      const formData = new FormData()
      formData.append('files', file)

      const res = await apiClient.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: opts?.signal,
        onUploadProgress: (e) => {
          fileLoaded[fileIndex] = e.loaded ?? 0
          reportProgress()
        },
      })
      return res.data as { message: string; files: Asset[] }
    }

    // Upload files with concurrency limit — individual failures don't block others
    const allFiles: Asset[] = []
    const errors: string[] = []
    for (let i = 0; i < files.length; i += MAX_CONCURRENT) {
      const batch = files.slice(i, i + MAX_CONCURRENT)
      const results = await Promise.allSettled(
        batch.map((file, j) => uploadOne(file, i + j))
      )
      for (let k = 0; k < results.length; k++) {
        const result = results[k]
        if (result.status === 'fulfilled') {
          allFiles.push(...result.value.files)
        } else {
          const failedFile = batch[k]
          errors.push(failedFile.name)
          // Mark failed file as fully loaded so progress doesn't stall
          fileLoaded[i + k] = fileTotalBytes[i + k]
          reportProgress()
        }
      }
    }

    if (errors.length > 0 && allFiles.length === 0) {
      throw new Error(`Failed to upload: ${errors.join(', ')}`)
    }

    const msg = errors.length > 0
      ? `Uploaded ${allFiles.length} file(s), ${errors.length} failed: ${errors.join(', ')}`
      : `Successfully uploaded ${allFiles.length} file(s)`
    return { message: msg, files: allFiles }
  },

  /** Upload files by signing one PUT URL per file and sending bytes directly
   *  to MinIO (no backend in the byte path). Same signature as upload() so
   *  callers can swap freely. Faster on big files: one wire hop instead of
   *  two, no uvicorn worker held hostage during the upload.
   *
   *  onSpeed is called with current average throughput in MB/s, computed
   *  from total bytes uploaded / wall-clock elapsed across all files. */
  async uploadDirect(
    projectId: string,
    files: File[],
    opts?: {
      folderId?: number
      uploadDate?: string
      onProgress?: (pct: number) => void
      onSpeed?: (mbps: number) => void
      signal?: AbortSignal
    },
  ) {
    const MAX_CONCURRENT = 8
    // Dedicated axios instance for PUTs — we don't want the shared apiClient
    // adding Authorization headers (MinIO rejects them; the signature IS the auth).
    const axios = (await import('axios')).default

    const fileLoaded = new Array(files.length).fill(0)
    const fileTotalBytes = files.map(f => f.size || 0)
    const grandTotal = fileTotalBytes.reduce((a, b) => a + b, 0)
    const startTime = performance.now()
    const reportProgress = () => {
      const loaded = fileLoaded.reduce((a: number, b: number) => a + b, 0)
      if (grandTotal > 0 && opts?.onProgress) {
        opts.onProgress(Math.round((loaded / grandTotal) * 100))
      }
      if (opts?.onSpeed) {
        const elapsedSec = (performance.now() - startTime) / 1000
        const mbps = elapsedSec > 0 ? (loaded / (1024 * 1024)) / elapsedSec : 0
        opts.onSpeed(mbps)
      }
    }

    const uploadOne = async (file: File, fileIndex: number) => {
      // Step 1 — ask backend for a presigned PUT URL + asset placeholder.
      const startResp = await apiClient.post('/assets/start-direct-upload', {
        project_id: projectId,
        folder_id: opts?.folderId,
        upload_date: opts?.uploadDate,
        asset_type: 'footage',
        filename: file.name,
        file_size: file.size,
        file_type: file.type || undefined,
      })
      const {
        asset_id, filename, file_path, upload_url,
      } = startResp.data as {
        asset_id: number; filename: string; file_path: string;
        upload_url: string; expires_in: number;
      }

      // Step 2 — PUT the file directly to MinIO through the /minio-proxy/ path.
      // Progress tracked via axios; this is where the real time goes.
      await axios.put(upload_url, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        signal: opts?.signal,
        onUploadProgress: (e) => {
          fileLoaded[fileIndex] = e.loaded ?? 0
          reportProgress()
        },
        // No auth header. The presigned URL's signature is the credential.
        transformRequest: [(data) => data],
      })

      // Step 3 — tell backend the bytes landed.
      const completeResp = await apiClient.post(
        `/assets/${asset_id}/complete-direct-upload`,
      )
      const { file_size: confirmedSize, status } = completeResp.data as {
        asset_id: number; status: string; file_size: number;
      }

      // Shape the response like the legacy endpoint so call sites can drop
      // into their existing state handlers.
      return {
        id: asset_id,
        filename,
        original_filename: file.name,
        file_path,
        file_size: confirmedSize,
        file_type: file.type || undefined,
        status,
        folder_id: opts?.folderId,
        project_id: projectId,
      }
    }

    const allFiles: Asset[] = []
    const errors: string[] = []
    for (let i = 0; i < files.length; i += MAX_CONCURRENT) {
      const batch = files.slice(i, i + MAX_CONCURRENT)
      const results = await Promise.allSettled(
        batch.map((file, j) => uploadOne(file, i + j)),
      )
      for (let k = 0; k < results.length; k++) {
        const result = results[k]
        if (result.status === 'fulfilled') {
          // uploadOne builds a nine-field stand-in from the presigned flow, not
          // a full Asset, and every consumer of uploadDirect treats it as one.
          // `any[]` hid that. This cast keeps the hole the same size and
          // visible; closing it means either returning the stored asset after
          // upload or teaching consumers to accept a partial.
          allFiles.push(result.value as Asset)
        } else {
          const failedFile = batch[k]
          errors.push(failedFile.name)
          fileLoaded[i + k] = fileTotalBytes[i + k]
          reportProgress()
        }
      }
    }

    if (errors.length > 0 && allFiles.length === 0) {
      throw new Error(`Failed to upload: ${errors.join(', ')}`)
    }

    const msg = errors.length > 0
      ? `Uploaded ${allFiles.length} file(s), ${errors.length} failed: ${errors.join(', ')}`
      : `Successfully uploaded ${allFiles.length} file(s)`
    return { message: msg, files: allFiles }
  },
}

// ─── Collection API ──────────────────────────────────────────────────────────

export const collectionApi = {
  /** List collections for a project. */
  async list(projectId: string): Promise<CollectionListResponse> {
    const res = await apiClient.get('/collections/', { params: { project_id: projectId } })
    return res.data
  },

  /** Get a single collection. */
  async get(collectionId: number): Promise<Collection> {
    const res = await apiClient.get(`/collections/${collectionId}`)
    return res.data
  },

  /** Create a new collection. */
  async create(data: {
    project_id: string
    name: string
    description?: string
    icon?: string
    color?: string
    filter_config: CollectionFilterConfig
    sort_field?: string
    sort_dir?: string
  }): Promise<Collection> {
    const res = await apiClient.post('/collections/', data)
    return res.data
  },

  /** Update a collection. */
  async update(collectionId: number, data: Record<string, unknown>): Promise<Collection> {
    const res = await apiClient.patch(`/collections/${collectionId}`, data)
    return res.data
  },

  /** Delete a collection. */
  async delete(collectionId: number): Promise<void> {
    await apiClient.delete(`/collections/${collectionId}`)
  },

  /** Get assets matching a collection's filters. */
  async getAssets(collectionId: number, opts: { offset?: number; limit?: number } = {}): Promise<AssetListResponse> {
    const params: Record<string, unknown> = {}
    if (opts.offset !== undefined) params.offset = opts.offset
    params.limit = opts.limit ?? 200
    const res = await apiClient.get(`/collections/${collectionId}/assets`, { params })
    return res.data
  },
}
